import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router-dom';
import { Popup } from 'semantic-ui-react';
import { ChevronDown } from 'lucide-react';

import { ReadOnlyContext } from '../grid/ReadOnlyContext';
import { CONTEXT_MENU_REGISTRY } from '../../../constants/contextMenuEntries';
import { SystemOnlyIcon, SystemOnlyPopup } from '../SystemOnlyBadge';
import styles from './EntityActionsDropdown.module.css';

export type EntityActionsMenuItemAction = {
  type?: 'action';
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  onSelect?: () => void | Promise<void>;
  linkTo?: string;
  href?: string;
  target?: string;
  disabled?: boolean;
  /** Shown on hover and focus, and read out as the item's description */
  tooltip?: string;
  closeOnSelect?: boolean;
  /** When true, this item is hidden for read-only (viewer) users */
  writeAction?: boolean;
  /** Only system-level credentials get this item: the icon follows the label and explains why */
  systemOnly?: boolean;
};

export type EntityActionsMenuItemSeparator = {
  type: 'separator';
  key: string;
};

export type EntityActionsMenuItem = EntityActionsMenuItemAction | EntityActionsMenuItemSeparator;

export type EntityActionsDropdownProps = {
  triggerLabel: React.ReactNode;
  menuItems: EntityActionsMenuItem[];
  className?: string;
  variant?: 'primary' | 'secondary';
};

export const EntityActionsDropdown: React.FC<EntityActionsDropdownProps> = ({
  triggerLabel,
  menuItems,
  className,
  variant = 'primary'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isReadOnly = useContext(ReadOnlyContext);
  const idPrefix = useId();

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      if (!dropdownRef.current) {
        return;
      }

      const target = event.target as Node | null;
      if (target && dropdownRef.current.contains(target)) {
        return;
      }

      closeMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  const safeCloneIcon = useCallback((icon?: React.ReactNode) => {
    if (!icon) {
      return null;
    }

    if (React.isValidElement(icon)) {
      const existingClassName = icon.props.className ? `${icon.props.className} ${styles.actionsDropdownItemIcon}` : styles.actionsDropdownItemIcon;
      return React.cloneElement(icon, {
        className: existingClassName,
        'aria-hidden': icon.props['aria-hidden'] ?? true
      });
    }

    return icon;
  }, []);

  const handleItemActivate = useCallback(
    (item: EntityActionsMenuItemAction) => {
      if (item.disabled) {
        return;
      }

      const maybePromise = item.onSelect?.();
      const shouldClose = item.closeOnSelect !== false;

      if (maybePromise && typeof (maybePromise as Promise<unknown>).then === 'function') {
        (maybePromise as Promise<unknown>).finally(() => {
          if (shouldClose) {
            closeMenu();
          }
        });
      } else if (shouldClose) {
        closeMenu();
      }
    },
    [closeMenu]
  );

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const containerClassName = className
    ? `${styles.actionsDropdown} ${className}`
    : styles.actionsDropdown;

  const buttonClassName = variant === 'secondary'
    ? `${styles.actionsDropdownButton} ${styles.actionsDropdownButtonSecondary}`
    : styles.actionsDropdownButton;

  // Filter out falsy items and consecutive separators
  const visibleMenuItems = useMemo(() => {
    // First filter out falsy items and writeAction items when read-only
    const filtered = menuItems.filter((item) => {
      if (!item) return false;
      if (isReadOnly && item.type !== 'separator') {
        const actionItem = item as EntityActionsMenuItemAction;
        // Three-source writeAction check: direct prop → registry lookup by key
        const isWrite = actionItem.writeAction
          || CONTEXT_MENU_REGISTRY.get(actionItem.key)?.writeAction;
        if (isWrite) return false;
      }
      return true;
    });
    // Remove consecutive separators and leading/trailing separators
    const withoutConsecutive = filtered.filter((item, index) => {
      if (item.type === 'separator') {
        const prevItem = filtered[index - 1];
        // Remove if previous item is also a separator
        if (prevItem?.type === 'separator') {
          return false;
        }
      }
      return true;
    });
    // Remove leading separator
    if (withoutConsecutive[0]?.type === 'separator') {
      withoutConsecutive.shift();
    }
    // Remove trailing separator
    if (withoutConsecutive[withoutConsecutive.length - 1]?.type === 'separator') {
      withoutConsecutive.pop();
    }
    return withoutConsecutive;
  }, [menuItems, isReadOnly]);

  const renderMenuItem = (item: EntityActionsMenuItem) => {
    // Handle separator type
    if (item.type === 'separator') {
      return (
        <div
          key={item.key}
          className={styles.actionsDropdownSeparator}
          role="separator"
        />
      );
    }

    // Handle action type (default)
    const actionItem = item as EntityActionsMenuItemAction;
    const iconNode = safeCloneIcon(actionItem.icon);
    const tooltipId = actionItem.tooltip
      ? `${idPrefix}-${actionItem.key}-tooltip`
      : undefined;
    // Hidden nodes still count for aria-describedby, so screen readers get the tooltip text
    const tooltipNode = tooltipId ? (
      <span id={tooltipId} hidden>
        {actionItem.tooltip}
      </span>
    ) : null;
    const sharedProps = {
      key: actionItem.key,
      className: `${styles.actionsDropdownItem} ${actionItem.disabled ? styles.actionsDropdownItemDisabled : ''}`,
      role: 'menuitem' as const,
      'aria-describedby': tooltipId
    };
    const withTooltip = (element: React.ReactElement) =>
      actionItem.systemOnly ? (
        <SystemOnlyPopup key={actionItem.key}>{element}</SystemOnlyPopup>
      ) : actionItem.tooltip ? (
        <Popup
          key={actionItem.key}
          content={actionItem.tooltip}
          on={['hover', 'focus']}
          position="left center"
          trigger={element}
        />
      ) : (
        element
      );
    const labelNode = (
      <>
        <span>{actionItem.label}</span>
        {actionItem.systemOnly && <SystemOnlyIcon />}
      </>
    );

    // External link (href)
    if (actionItem.href) {
      return withTooltip(
        <a
          {...sharedProps}
          href={actionItem.href}
          target={actionItem.target}
          rel={actionItem.target === '_blank' ? 'noopener noreferrer' : undefined}
          onClick={(event) => {
            if (actionItem.disabled) {
              event.preventDefault();
              return;
            }
            actionItem.onSelect?.();
            if (actionItem.closeOnSelect !== false) {
              closeMenu();
            }
          }}
          aria-disabled={actionItem.disabled || undefined}
        >
          {iconNode}
          {labelNode}
          {tooltipNode}
        </a>
      );
    }

    // Internal link (linkTo)
    if (actionItem.linkTo) {
      return withTooltip(
        <Link
          {...sharedProps}
          to={actionItem.linkTo}
          onClick={(event) => {
            if (actionItem.disabled) {
              event.preventDefault();
              return;
            }
            actionItem.onSelect?.();
            if (actionItem.closeOnSelect !== false) {
              closeMenu();
            }
          }}
          aria-disabled={actionItem.disabled || undefined}
        >
          {iconNode}
          {labelNode}
          {tooltipNode}
        </Link>
      );
    }

    // Button (onClick handler)
    return withTooltip(
      <button
        {...sharedProps}
        type="button"
        onClick={() => handleItemActivate(actionItem)}
        disabled={actionItem.disabled}
        aria-disabled={actionItem.disabled || undefined}
      >
        {iconNode}
        {labelNode}
        {tooltipNode}
      </button>
    );
  };

  return (
    <div className={containerClassName} ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleMenu}
        className={buttonClassName}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span>{triggerLabel}</span>
        <ChevronDown
          className={`${styles.actionsDropdownChevron} ${isOpen ? styles.actionsDropdownChevronOpen : ''}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className={styles.actionsDropdownMenu} role="menu">
          {visibleMenuItems.map(renderMenuItem)}
        </div>
      )}
    </div>
  );
};
